import { api } from '@organization/api'
import { useSessionContext } from '@shared/hooks'
import { showApiErrors } from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import {
  App,
  Button,
  Card,
  Col,
  Flex,
  Form,
  GlobalToken,
  Row,
  theme,
  Typography
} from 'antd'
import { AggregationColor } from 'antd/es/color-picker/color'
import { AliasToken } from 'antd/es/theme/interface'
import { useEffect } from 'react'

import { useTranslation } from 'react-i18next'

import { LabeledColorPicker } from '../components'

type DefaultColorKeys = keyof Pick<
  AliasToken,
  | 'colorPrimary'
  | 'colorSuccess'
  | 'colorWarning'
  | 'colorError'
  | 'colorInfo'
  | 'colorTextBase'
>

const TOKENS_COLOR_SETTINGS_FORM = 'tokens-color-settings-form'

const getDefaultColors = (
  token: GlobalToken
): Record<DefaultColorKeys, string> => {
  return {
    colorError: token.colorError,
    colorInfo: token.colorInfo,
    colorPrimary: token.colorPrimary,
    colorSuccess: token.colorSuccess,
    colorTextBase: token.colorTextBase,
    colorWarning: token.colorWarning
  }
}

export const Theme = () => {
  const [form] = Form.useForm<AliasToken>()
  const { token } = theme.useToken()
  const { message } = App.useApp()
  const { updateUserData, user } = useSessionContext()
  const { t } = useTranslation()

  const tokenValue =
    user?.organization?.settings?.token || getDefaultColors(token)

  const { mutate, isPending } = useMutation({
    mutationFn: api.organizations.patchOrganizationsChangById,

    onSuccess: () => {
      message.success(t('theme.updated'))
      updateUserData()
    },

    onError: (error) => showApiErrors(error, form)
  })

  const onFinish = (values: AliasToken) => {
    const userOrganization = user?.organization

    if (userOrganization) {
      mutate({
        body: {
          ...userOrganization,
          settings: {
            token: values
          }
        },
        id: userOrganization.id
      })
    }
  }

  useEffect(() => {
    form.setFieldsValue(tokenValue)
  }, [tokenValue])

  return (
    <Flex vertical gap={20}>
      <Typography.Title level={4}>{t('theme.header')}</Typography.Title>

      <Row>
        <Col span={12} xl={8}>
          <Card
            title={t('theme.title')}
            actions={[
              <Flex key={1} gap={8} justify="end">
                <Button
                  form={TOKENS_COLOR_SETTINGS_FORM}
                  onClick={() => form.resetFields()}
                >
                  {t('button.reset')}
                </Button>

                <Button
                  type="primary"
                  htmlType="submit"
                  form={TOKENS_COLOR_SETTINGS_FORM}
                  loading={isPending}
                >
                  {t('button.save')}
                </Button>
              </Flex>
            ]}
          >
            <Form
              layout="vertical"
              form={form}
              initialValues={tokenValue}
              name={TOKENS_COLOR_SETTINGS_FORM}
              onFinish={onFinish}
            >
              {Object.keys(tokenValue).map((colorKey) => (
                <Form.Item
                  key={colorKey}
                  name={colorKey}
                  getValueFromEvent={(color: AggregationColor) =>
                    '#' + color.toHex()
                  }
                >
                  <LabeledColorPicker nameColor={colorKey} />
                </Form.Item>
              ))}
            </Form>
          </Card>
        </Col>
      </Row>
    </Flex>
  )
}
